/**
 * Approvals analytics utilities
 *
 * Shared constants and helpers for analytics events fired across the Approvals workflow.
 * All approval-related events should use the APPROVALS_ANALYTICS_SOURCE constant as their source.
 */
import type { Approver } from './ApprovalsContext.tsx';

/**
 * Analytics source identifier for all Approvals workflow events.
 * Used as the `source` attribute in UI, Track, and Screen events.
 */
export const APPROVALS_ANALYTICS_SOURCE = 'ConfluenceApprovalsWorkflow';

export const APPROVALS_INVOKE_ROVO_AGENT_CLICKED_EVENT = {
	type: 'sendUIEvent',
	data: {
		action: 'clicked',
		actionSubject: 'button',
		actionSubjectId: 'invokeRovoAgent',
		source: APPROVALS_ANALYTICS_SOURCE,
	},
} as const;

/**
 * Describes a decision change being applied by the current user.
 *
 * Pass this to `computeReviewStatusCounts` to get counts that reflect the
 * decision *after* it is applied, before the BE data is refetched.
 *
 * - `previousDecision`: the approver's current status in the BE data (`Approver['status']`).
 *   Omit (or pass `'PENDING_REVIEW'`) for a first-time submission. `'REVIEW_CANCELED'` is
 *   not a counted bucket and is treated as a no-op decrement.
 * - `newDecision`: the decision being submitted.
 */
export type DecisionChange = {
	previousDecision?: Approver['status'];
	newDecision: 'REVIEWER_APPROVED' | 'REVIEWER_DENIED';
};

/**
 * Computes per-status counts from an array of approvers.
 *
 * Returns an object matching the `updatedReviewStatus` / `currentReviewStatus`
 * shape required by the Approvals Analytics Spec.
 *
 * Pass an optional `decisionChange` to adjust the counts to reflect a decision
 * that has just been submitted but is not yet reflected in the `approvers` data
 * (i.e. before the BE response is refetched). The counts for the previous bucket
 * are decremented and the counts for the new bucket are incremented.
 */
export const computeReviewStatusCounts = (
	approvers: Approver[],
	decisionChange?: DecisionChange,
): { approved: number; declined: number; pending: number; total: number } => {
	const counts = approvers.reduce(
		(acc, approver) => {
			if (approver.status === 'REVIEWER_APPROVED') {
				acc.approved++;
			} else if (approver.status === 'REVIEWER_DENIED') {
				acc.declined++;
			} else if (approver.status === 'PENDING_REVIEW') {
				acc.pending++;
			}
			return acc;
		},
		{ approved: 0, declined: 0, pending: 0, total: approvers.length },
	);

	if (decisionChange) {
		const { previousDecision = 'PENDING_REVIEW', newDecision } = decisionChange;

		// Decrement the previous bucket (REVIEW_CANCELED is not a counted bucket; treat as no-op)
		if (previousDecision === 'REVIEWER_APPROVED') {
			counts.approved--;
		} else if (previousDecision === 'REVIEWER_DENIED') {
			counts.declined--;
		} else if (previousDecision === 'PENDING_REVIEW') {
			counts.pending--;
		}

		// Increment the new bucket
		if (newDecision === 'REVIEWER_APPROVED') {
			counts.approved++;
		} else {
			counts.declined++;
		}
	}

	return counts;
};

/**
 * Shape of a mutation error extension from the GraphQL response.
 *
 * The AGG schema returns `extensions` as a single object on each error
 * (not an array). Some older call sites may still pass an array shape,
 * so both are accepted by `getFailureStatusCode`.
 */
type MutationErrorExtension = {
	errorType?: string | null;
	statusCode?: number | null;
};

type MutationError = {
	extensions?: MutationErrorExtension | MutationErrorExtension[] | null;
	message?: string | null;
};

/**
 * Extracts the first `statusCode` from a mutation error's extensions.
 *
 * Accepts the AGG schema shape where `extensions` is a single object
 * (`{ errorType, statusCode }`), as well as the legacy array shape
 * (`[{ errorType, statusCode }]`).
 *
 * Returns null when the error does not match the expected shape or contains
 * no statusCode.
 */
export const getFailureStatusCode = (error: unknown): number | null => {
	if (error == null || typeof error !== 'object') {
		return null;
	}

	const mutationError = error as MutationError;
	const { extensions } = mutationError;

	if (extensions == null) {
		return null;
	}

	const ext = Array.isArray(extensions) ? extensions[0] : extensions;
	if (ext == null) {
		return null;
	}

	const { statusCode } = ext;
	if (typeof statusCode === 'number') {
		return statusCode;
	}

	return null;
};
